import axios, { type AxiosError, type InternalAxiosRequestConfig, AxiosInstance } from 'axios';
import { useAuthStore } from '@/stores/authStore';

// Microservice base URLs
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:3001/api';
const LISTING_SERVICE_URL = import.meta.env.VITE_LISTING_SERVICE_URL || 'http://localhost:3002/api';
const BOOKING_SERVICE_URL = import.meta.env.VITE_BOOKING_SERVICE_URL || 'http://localhost:3003/api';

// Create Axios instances for each microservice
export const authAPI: AxiosInstance = axios.create({
  baseURL: AUTH_SERVICE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const listingAPI: AxiosInstance = axios.create({
  baseURL: LISTING_SERVICE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const bookingAPI: AxiosInstance = axios.create({
  baseURL: BOOKING_SERVICE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh queue for handling concurrent requests during refresh
let isRefreshing = false;
let failedQueue: Array<{
  onSuccess: (token: string) => void;
  onError: (error: AxiosError) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.onError(error);
    } else {
      prom.onSuccess(token || '');
    }
  });
  failedQueue = [];
};

// Attach token interceptor to all APIs
const attachTokenInterceptor = (api: AxiosInstance) => {
  api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );
};

// Response interceptor with token refresh logic
const attachRefreshInterceptor = (api: AxiosInstance) => {
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // If 401 and we haven't already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        const refreshToken = useAuthStore.getState().refreshToken;

        if (refreshToken && !isRefreshing) {
          isRefreshing = true;
          originalRequest._retry = true;

          try {
            const response = await authAPI.post('/v1/auth/refresh', { refreshToken });

            const { accessToken, refreshToken: newRefreshToken } = response.data.data;
            useAuthStore.getState().refreshAuth(accessToken, newRefreshToken);

            processQueue(null, accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          } catch (err) {
            // Refresh failed, logout and redirect
            processQueue(err as AxiosError, null);
            useAuthStore.getState().logout();
            window.location.href = '/login';
            return Promise.reject(err);
          } finally {
            isRefreshing = false;
          }
        }

        // If already refreshing, queue this request
        if (isRefreshing && refreshToken) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              onSuccess: (token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(api(originalRequest));
              },
              onError: (error: AxiosError) => {
                reject(error);
              },
            });
          });
        }

        // No refresh token — only redirect if user had an access token
        // (don't redirect for failed login/register attempts that return 401)
        const currentToken = useAuthStore.getState().token;
        if (currentToken) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    },
  );
};

// Apply interceptors to all API instances
[authAPI, listingAPI, bookingAPI].forEach((api) => {
  attachTokenInterceptor(api);
  attachRefreshInterceptor(api);
});

// Default export for backwards compatibility
const api = authAPI;
export default api;
